import type { ChronicleEntry, WorldState } from "../core/types";
import { monsterDefinition } from "../depth/combat";
import { selectDisarmingKit } from "../depth/disarming-kit";
import { isCanonicalRoadRations } from "../depth/road-rations";
import { copperhornResearchClue, inkcapResearchClue, isValidFieldResearchState, moonhowlResearchClue } from "../depth/field-research";
import { counterDuelHabitText, counterDuelPatternBreakText, counterDuelStanceLabel, counterDuelTellText, projectCounterDuelSpeciesHabit } from "../depth/counter-duel";
import { projectSuccessorQuestLead, type QuestLeadPhase } from "../depth/quest-lead";
import { abilityExperienceCeiling, abilityExperienceFloor, describeCompletedQuestReward, maximumAbilities, questObjectiveRuleLabel, secretTechniqueInsightRequired, weaponUseExperienceFloors } from "../depth/rpg";
import { encounterThreatBand, encounterThreatBandLabel } from "../depth/threat";
import type { AbilityEffect, AbilityKind, CopperhornApplicationV1, CopperhornBurningTickV1, CopperhornFinalEmberV1, CounterDuelHabitKnowledge, CounterDuelState, EquipmentSlot, FalseTreasureAftereffectV1, FalseTreasureApplicationV1, ItemModifier, ItemState, MoonhowlApplicationV1, MoonhowlStrikeV1, ObjectiveStatus, QuestStatus } from "../depth/types";
import type { PatternBreakObserverReactionV1 } from "./pattern-break-observer-reaction";
import { projectCounterDuelPatternBreakSignature } from "./pattern-break-signature";
import { projectAtlasPartyMarker, type AtlasPartyMarkerV1 } from "./atlas-party-marker";

export type InspectionView = "watch" | "adventure" | "map" | "inventory" | "journal" | "codex" | "spellbook" | "hall";

export const inspectionViews: readonly InspectionView[] = ["watch", "adventure", "map", "inventory", "journal", "codex", "spellbook", "hall"];
export const maximumCodexEntries = 24;

export function projectCounterDuelSummary(
  heroName: string,
  duel: CounterDuelState,
  habit: CounterDuelHabitKnowledge,
  observerReaction: PatternBreakObserverReactionV1 | null = null,
): string {
  const rules = "Rules: Rush defeats Feint; Feint defeats Ward; Ward defeats Rush. First to 2; after round 5, the leader wins and an equal score draws.";
  const stakes = `Stakes: victory grants ${duel.stakes.victoryExperience} experience and ${duel.stakes.victoryGold} gold; defeat costs ${duel.stakes.defeatDamage} health; a draw changes neither.`;
  const openingRule = "Pattern Break rule: two consecutive correct predictions confirmed by their public live tells; any other round resets the opening. It adds no bonus score or reward; the correct counter still scores its ordinary point.";
  const opening = duel.patternBreak === undefined
    ? "Pattern Break unavailable in this legacy duel."
    : counterDuelPatternBreakText(duel.patternBreak);
  const signature = projectCounterDuelPatternBreakSignature(duel);
  const signatureText = signature === null
    ? ""
    : ` Species signature: ${signature.speciesName}; presentation only. The correct counter scores its ordinary point and victory keeps the standard reward.`;
  const observerText = observerReaction === null
    ? ""
    : ` Observer ${observerReaction.companion.name} · ${observerReaction.companion.role} · ${observerReaction.companion.status} · health ${observerReaction.companion.health}/${observerReaction.companion.maxHealth}. ${observerReaction.gesture.caption} Presentation only; no dialogue or mechanical effect.`;
  const latest = duel.history.at(-1);
  const revealed = latest === undefined
    ? "No completed exchange yet."
    : `Latest completed exchange: predicted ${counterDuelStanceLabel(latest.prediction)}; ${heroName} answered ${counterDuelStanceLabel(latest.heroStance)}; ${duel.opponentName} revealed ${counterDuelStanceLabel(latest.opponentStance)}; ${latest.result === "hero" ? `${heroName} won the point` : latest.result === "opponent" ? `${duel.opponentName} won the point` : "the point tied"}; score ${latest.heroScore} to ${latest.opponentScore}.${latest.patternBreak?.triggered === true ? " Pattern Break triggered from two consecutive confirmed live-tell reads; standard reward only." : latest.patternBreak?.openingGain === 1 ? " Opening advanced to 1 of 2 confirmed reads." : latest.patternBreak?.reset === true ? " The opening reset." : ""}`;
  const phase = duel.outcome === "ongoing"
    ? `Round ${duel.round}. Score ${heroName} ${duel.heroScore} to ${duel.opponentScore} ${duel.opponentName}. Public tell: ${counterDuelTellText(duel.tell)}. The rival's current stance remains hidden.`
    : `Final outcome: ${duel.outcome}. Final score ${heroName} ${duel.heroScore} to ${duel.opponentScore} ${duel.opponentName}.`;
  return `Pattern Duel. ${rules} ${stakes} ${openingRule} ${phase} ${counterDuelHabitText(habit)}. ${opening}.${signatureText}${observerText} ${revealed}`;
}

export interface MapViewProjection {
  party: AtlasPartyMarkerV1 | null;
  currentPlace: string;
  currentLeg: string | null;
  destination: string | null;
  progress: string;
  nextLegDanger: { score: number; band: string; label: string } | null;
  discovered: string;
  terrain: string;
  questLead: { id: string; locationId: string; locationName: string; phase: QuestLeadPhase; discovered: boolean } | null;
}

export interface InventoryModifierView {
  name: ItemModifier;
  value: number;
}

export interface InventoryItemView {
  id: string;
  name: string;
  kind: ItemState["kind"];
  slot: EquipmentSlot | null;
  rarity: ItemState["rarity"];
  quantity: number;
  equippedSlot: EquipmentSlot | null;
  modifiers: readonly InventoryModifierView[];
  restorative: string | null;
  dungeonTool: string | null;
  food: string | null;
  useMastery: {
    level: number;
    experience: number;
    nextExperience: number | null;
    latestSource: { combatId: string; resolvedTick: number; outcome: "victory" | "defeat" | "stalemate" } | null;
  } | null;
}

export interface InventoryViewProjection {
  heroName: string;
  classAndLevel: string;
  gold: number;
  stackCount: number;
  itemCount: number;
  equippedCount: number;
  items: readonly InventoryItemView[];
}

export interface QuestView {
  id: string;
  title: string;
  status: ObjectiveStatus | QuestStatus;
  statusLabel: string;
  objectives: readonly { id: string; description: string; progress: string; status: ObjectiveStatus; ruleLabel: string }[];
}

export interface JournalViewProjection {
  questTitle: string;
  questSummary: string;
  questLead: { id: string; locationId: string; locationName: string; phase: QuestLeadPhase; discovered: boolean } | null;
  quests: readonly QuestView[];
  completedChapters: readonly {
    id: string;
    ordinal: number;
    title: string;
    fulfilledTick: number;
    objectiveCount: number;
    rewardSummary: string;
  }[];
  entries: readonly ChronicleEntry[];
}

export type CodexVisualKey =
  | "lantern-wolf"
  | "mossback-brute"
  | "river-wyrmling"
  | "inkcap-mimic"
  | "copperhorn"
  | "unknown";

export interface CodexTechniqueView {
  id: string;
  name: string;
  effect: AbilityEffect;
  manaCost: number;
  potency: number;
  level: number;
  experience: number;
  experienceFloor: number;
  experienceCeiling: number;
  uses: number;
  discoveryTick: number;
}

export interface CodexDiscoveryOutcomeView {
  disposition: "deferred-capacity" | "rejected";
  reason: string;
  abilityName: string;
  effect: AbilityEffect | null;
  manaCost: number | null;
  potency: number | null;
  recordedTick: number;
  thresholdTick: number | null;
  repertoireCount: number;
  repertoireLimit: number;
}

export interface CodexMonsterView {
  monsterId: string;
  monsterName: string;
  visualKey: CodexVisualKey;
  encounters: number;
  victories: number;
  insight: number;
  requiredInsight: number;
  remainingVictories: number;
  habit: CounterDuelHabitKnowledge | null;
  techniqueStatus: "studying" | "learned" | "held" | "rejected" | "unverified";
  technique: CodexTechniqueView | null;
  discoveryOutcome: CodexDiscoveryOutcomeView | null;
  observedOnly?: true;
  fieldResearch?: CodexFieldResearchView;
}

interface CodexResearchPresentation {
  title: string;
  progress: 0 | 1 | 2;
  clue: string | null;
  applicationText: string | null;
  aftereffectText: string | null;
}

export type CodexFieldResearchView = CodexResearchPresentation & (
  | { taskId: "inkcap:false-treasure@1"; application: FalseTreasureApplicationV1 | null; aftereffect: FalseTreasureAftereffectV1 | null }
  | { taskId: "lantern-wolf:moonhowl@1"; application: MoonhowlApplicationV1 | null; aftereffect: MoonhowlStrikeV1 | null }
  | { taskId: "copperhorn:final-ember@1"; application: CopperhornApplicationV1 | null; firstTick: CopperhornBurningTickV1 | null; firstTickText: string | null; aftereffect: CopperhornFinalEmberV1 | null }
);

export function projectInkcapFieldResearch(state: WorldState): Extract<CodexFieldResearchView, { taskId: "inkcap:false-treasure@1" }> {
  const source = state.depth.fieldResearch;
  const valid = isValidFieldResearchState(source, state.depth.hero.id, state.depth.tick);
  const application = valid ? source.inkcap.application : null;
  const aftereffect = valid ? source.inkcap.aftereffect : null;
  return {
    taskId: "inkcap:false-treasure@1", title: "Study False Treasure",
    progress: application === null ? 0 : aftereffect === null ? 1 : 2,
    clue: aftereffect === null ? null : inkcapResearchClue,
    application, aftereffect,
    applicationText: application === null ? null
      : `T${application.sourceTick} · Combat turn ${application.sourceTurn}: False Treasure applied poison to the hero · potency ${application.potency} · ${application.duration} turns · HP ${application.targetHealthAfter} after application.`,
    aftereffectText: aftereffect === null ? null
      : `T${aftereffect.sourceTick} · Combat turn ${aftereffect.sourceTurn}: poison harmed the hero before acting · HP ${aftereffect.healthBefore}→${aftereffect.healthAfter} (−${aftereffect.amount}) · duration ${aftereffect.durationBefore}→${aftereffect.durationAfter}.`,
  };
}

export function projectMoonhowlFieldResearch(state: WorldState): Extract<CodexFieldResearchView, { taskId: "lantern-wolf:moonhowl@1" }> {
  const source = state.depth.fieldResearch;
  const valid = isValidFieldResearchState(source, state.depth.hero.id, state.depth.tick);
  const application = valid ? source.moonhowl.application : null;
  const aftereffect = valid ? source.moonhowl.aftereffect : null;
  return {
    taskId: "lantern-wolf:moonhowl@1", title: "Study Moonhowl",
    progress: application === null ? 0 : aftereffect === null ? 1 : 2,
    clue: aftereffect === null ? null : moonhowlResearchClue,
    application, aftereffect,
    applicationText: application === null ? null
      : `T${application.sourceTick} · Combat turn ${application.sourceTurn}: Moonhowl weakened the hero · potency ${application.potency} · ${application.duration} turns · Hero HP ${application.targetHealthAfter} after application.`,
    aftereffectText: aftereffect === null ? null
      : `T${aftereffect.sourceTick} · Combat turn ${aftereffect.sourceTurn}: weakening remained before the hero's strike · Hero HP ${aftereffect.healthBefore}→${aftereffect.healthAfter} (no status damage) · duration ${aftereffect.durationBefore}→${aftereffect.durationAfter} · raw-power penalty ${aftereffect.potency}. The hero then struck a foe · Foe HP ${aftereffect.targetHealthBefore}→${aftereffect.targetHealthAfter} (−${aftereffect.damage}). This is the recorded hit, not an unweakened damage comparison.`,
  };
}

export function projectCopperhornFieldResearch(state: WorldState): Extract<CodexFieldResearchView, { taskId: "copperhorn:final-ember@1" }> {
  const source = state.depth.fieldResearch;
  const valid = isValidFieldResearchState(source, state.depth.hero.id, state.depth.tick);
  const application = valid ? source.copperhorn.application : null;
  const firstTick = valid ? source.copperhorn.firstTick : null;
  const aftereffect = valid ? source.copperhorn.aftereffect : null;
  return {
    taskId: "copperhorn:final-ember@1", title: "Study Final Ember",
    progress: application === null ? 0 : aftereffect === null ? 1 : 2,
    clue: aftereffect === null ? null : copperhornResearchClue,
    application, firstTick, aftereffect,
    applicationText: application === null ? null
      : `T${application.sourceTick} · Combat turn ${application.sourceTurn}: Bellmetal Charge applied burning to the hero · potency ${application.potency} · ${application.duration} turns · Hero HP ${application.targetHealthAfter} after application.`,
    firstTickText: firstTick === null ? null
      : `T${firstTick.sourceTick} · Combat turn ${firstTick.sourceTurn}: the first burn harmed the hero before action resolution · Hero HP ${firstTick.healthBefore}→${firstTick.healthAfter} (−${firstTick.amount}) · duration 2→1. Supporting evidence, not another progress mark.`,
    aftereffectText: aftereffect === null ? null
      : `T${aftereffect.sourceTick} · Combat turn ${aftereffect.sourceTurn}: the final burn expired but still harmed the hero before action resolution · Hero HP ${aftereffect.healthBefore}→${aftereffect.healthAfter} (−${aftereffect.amount}) · duration 1→0.`,
  };
}

export interface CodexViewProjection {
  recordedCount: number;
  learnedCount: number;
  heldCount: number;
  rejectedCount: number;
  unverifiedCount: number;
  hiddenCount: number;
  monsters: readonly CodexMonsterView[];
}

export interface SpellbookProvenanceView {
  monsterName: string;
  discoveryTick: number;
}

export interface SpellbookAbilityView {
  id: string;
  name: string;
  kind: AbilityKind;
  effect: AbilityEffect;
  level: number;
  experience: number;
  experienceFloor: number;
  experienceCeiling: number;
  masteryCurrent: number;
  masterySpan: number;
  experienceToNext: number;
  mastered: boolean;
  battleUses: number;
  manaCost: number;
  potency: number;
  provenanceStatus: "not-applicable" | "verified" | "unverified";
  provenance: SpellbookProvenanceView | null;
}

export interface SpellbookBreakthroughView {
  abilityId: string;
  abilityName: string;
  nextLevel: number;
  experienceToNext: number;
}

export interface SpellbookViewProjection {
  abilityCount: number;
  spellCount: number;
  techniqueCount: number;
  secretCount: number;
  masteredCount: number;
  totalBattleUses: string;
  hiddenCount: number;
  closestBreakthrough: SpellbookBreakthroughView | null;
  abilities: readonly SpellbookAbilityView[];
}

const codexVisualKeys = new Set<CodexVisualKey>([
  "lantern-wolf",
  "mossback-brute",
  "river-wyrmling",
  "inkcap-mimic",
  "copperhorn",
]);

const abilityKindOrder: Readonly<Record<AbilityKind, number>> = {
  spell: 0,
  technique: 1,
  secret: 2,
};

function locationName(state: WorldState, locationId: string | undefined): string | null {
  if (locationId === undefined) return null;
  return state.depth.atlas.locations.find((location) => location.id === locationId)?.name ?? null;
}

export function projectMapView(state: WorldState): MapViewProjection {
  const atlas = state.depth.atlas;
  const route = atlas.route;
  const fromName = route === null ? null : locationName(state, route.path[route.legIndex]);
  const toName = route === null ? null : locationName(state, route.path[route.legIndex + 1]);
  const nextLocationId = route?.path[route.legIndex + 1];
  const nextLocation = nextLocationId === undefined
    ? undefined
    : atlas.locations.find((location) => location.id === nextLocationId);
  const knownNextDanger = nextLocation !== undefined && atlas.discoveredLocationIds.includes(nextLocation.id)
    ? { score: nextLocation.danger, band: encounterThreatBand(nextLocation.danger), label: encounterThreatBandLabel(encounterThreatBand(nextLocation.danger)) }
    : null;
  const lead = projectSuccessorQuestLead(state.seed, atlas, state.depth.quest);
  return {
    party: projectAtlasPartyMarker(state.depth),
    currentPlace: locationName(state, atlas.currentLocationId) ?? state.scene.location,
    currentLeg: fromName !== null && toName !== null ? `${fromName} → ${toName}` : null,
    destination: route === null ? null : locationName(state, route.destinationId),
    progress: route === null
      ? "No route planned"
      : `${route.distanceTravelled}/${route.totalDistance} miles · ${Math.max(0, route.totalDistance - route.distanceTravelled)} remaining`,
    nextLegDanger: knownNextDanger,
    discovered: `${atlas.discoveredLocationIds.length}/${atlas.locations.length} mapped sites reached`,
    terrain: `${atlas.terrain.generator} · terrain v${atlas.terrain.version}`,
    questLead: lead === null ? null : {
      id: lead.id,
      locationId: lead.locationId,
      locationName: lead.locationName,
      phase: lead.phase,
      discovered: lead.discovered,
    },
  };
}

export function projectInventoryView(state: WorldState): InventoryViewProjection {
  const hero = state.depth.hero;
  const disarmingKit = selectDisarmingKit(hero);
  const equippedById = new Map<string, EquipmentSlot>();
  for (const [slot, itemId] of Object.entries(hero.equipment) as [EquipmentSlot, string | null][]) {
    if (itemId !== null) equippedById.set(itemId, slot);
  }
  const items = hero.inventory.map((item): InventoryItemView => ({
    id: item.id,
    name: item.name,
    kind: item.kind,
    slot: item.slot,
    rarity: item.rarity,
    quantity: item.quantity,
    equippedSlot: equippedById.get(item.id) ?? null,
    restorative: item.restorative === null ? null : "Combat self-use · restores ¼ max HP",
    dungeonTool: item.id === disarmingKit?.id
      ? "+2 to one disarm attempt · consumed on success or failure" : null,
    food: isCanonicalRoadRations(item, hero.id) ? "Two rations prepare Road Supper · next bound road battle's first direct hit: 25% less damage; stronger Guard takes precedence" : null,
    useMastery: item.useMastery === null ? null : {
      level: item.useMastery.level,
      experience: item.useMastery.experience,
      nextExperience: item.useMastery.level === 10 ? null : weaponUseExperienceFloors[item.useMastery.level] ?? null,
      latestSource: (() => {
        const latest = item.useMastery.receipts.at(-1);
        return latest === undefined ? null : { combatId: latest.combatId, resolvedTick: latest.resolvedTick, outcome: latest.outcome };
      })(),
    },
    modifiers: (Object.entries(item.modifiers) as [ItemModifier, number | undefined][])
      .flatMap(([name, value]) => value === undefined ? [] : [{ name, value }])
      .sort((left, right) => left.name < right.name ? -1 : left.name > right.name ? 1 : 0),
  }));
  return {
    heroName: hero.name,
    classAndLevel: `${hero.className} · Level ${hero.level}`,
    gold: hero.gold,
    stackCount: items.length,
    itemCount: items.reduce((total, item) => total + item.quantity, 0),
    equippedCount: items.filter((item) => item.equippedSlot !== null).length,
    items,
  };
}

export function projectJournalView(state: WorldState): JournalViewProjection {
  const quest = state.depth.quest;
  const statusLabel = (status: ObjectiveStatus | QuestStatus): string => status === "ready-to-fulfill"
    ? "Ready to fulfill"
    : status === "fulfilled"
      ? "Fulfilled"
      : status === "complete"
        ? "Complete"
        : status === "failed"
          ? "Failed"
          : "Active";
  const quests: QuestView[] = [
    {
      id: quest.id,
      title: quest.title,
      status: quest.status,
      statusLabel: statusLabel(quest.status),
      objectives: quest.objectives.map((objective) => ({
        id: objective.id,
        description: objective.description,
        progress: `${objective.current}/${objective.target}`,
        status: objective.status,
        ruleLabel: questObjectiveRuleLabel(objective.rule),
      })),
    },
    ...quest.subquests.map((subquest) => ({
      id: subquest.id,
      title: subquest.title,
      status: subquest.status,
      statusLabel: statusLabel(subquest.status),
      objectives: subquest.objectives.map((objective) => ({
        id: objective.id,
        description: objective.description,
        progress: `${objective.current}/${objective.target}`,
        status: objective.status,
        ruleLabel: questObjectiveRuleLabel(objective.rule),
      })),
    })),
  ];
  const latestCompletion = state.depth.completedQuests.at(-1);
  const lead = projectSuccessorQuestLead(state.seed, state.depth.atlas, quest);
  return {
    questTitle: quest.title,
    questSummary: quest.status === "fulfilled" && latestCompletion?.questInstanceId === quest.instanceId
      ? `Fulfilled at T${latestCompletion.fulfilledTick} · ${latestCompletion.objectiveIds.length} objectives complete · ${describeCompletedQuestReward(latestCompletion)}`
      : quest.summary,
    questLead: lead === null ? null : {
      id: lead.id,
      locationId: lead.locationId,
      locationName: lead.locationName,
      phase: lead.phase,
      discovered: lead.discovered,
    },
    quests,
    completedChapters: state.depth.completedQuests.slice().reverse().map((completion) => ({
      id: completion.id,
      ordinal: completion.questOrdinal,
      title: completion.title,
      fulfilledTick: completion.fulfilledTick,
      objectiveCount: completion.objectiveIds.length,
      rewardSummary: describeCompletedQuestReward(completion),
    })),
    entries: state.chronicle.slice(-12).reverse(),
  };
}

export function projectCodexView(state: WorldState): CodexViewProjection {
  const inkcapResearch = projectInkcapFieldResearch(state);
  const moonhowlResearch = projectMoonhowlFieldResearch(state);
  const copperhornResearch = projectCopperhornFieldResearch(state);
  const sortedLore = [...state.depth.hero.monsterLore].sort((left, right) => {
    const nameOrder = left.monsterName.localeCompare(right.monsterName, "en", { sensitivity: "base" });
    return nameOrder !== 0 ? nameOrder : left.monsterId < right.monsterId ? -1 : left.monsterId > right.monsterId ? 1 : 0;
  });
  const uniqueLore = [...new Map(sortedLore.map((entry) => [entry.monsterId, entry])).values()];
  const projected = uniqueLore.map((lore): CodexMonsterView => {
    const outcome = lore.learned
      ? state.depth.secretDiscoveryOutcomes.find((candidate) =>
          candidate.monsterId === lore.monsterId &&
          candidate.monsterName === lore.monsterName &&
          candidate.abilityId === lore.secretTechniqueId &&
          candidate.abilityName === lore.secretTechniqueName
        )
      : undefined;
    const admission = outcome === undefined || outcome.disposition === "rejected"
      ? undefined
      : state.depth.secretDiscoveryAdmissions.find((candidate) => candidate.outcomeId === outcome.id);
    const discovery = admission === undefined
      ? undefined
      : state.depth.discoveries.find((candidate) =>
          candidate.id === admission.discoveryId &&
          candidate.abilityId === outcome?.abilityId &&
          candidate.monsterId === outcome?.monsterId &&
          candidate.abilityName === outcome?.abilityName &&
          candidate.monsterName === outcome?.monsterName
        );
    const ability = (() => {
      if (discovery === undefined || outcome === undefined || outcome.mechanics === null) return undefined;
      const mechanics = outcome.mechanics;
      return state.depth.hero.abilities.find(
        (candidate) => candidate.id === outcome.abilityId
          && candidate.sourceMonsterId === outcome.monsterId
          && candidate.kind === "secret"
          && candidate.name === outcome.abilityName
          && candidate.effect === mechanics.effect
          && candidate.manaCost === mechanics.manaCost
          && candidate.potency === mechanics.potency,
      );
    })();
    const technique: CodexTechniqueView | null = ability === undefined || discovery === undefined
      ? null
      : {
          id: ability.id,
          name: ability.name,
          effect: ability.effect,
          manaCost: ability.manaCost,
          potency: ability.potency,
          level: ability.level,
          experience: ability.experience,
          experienceFloor: abilityExperienceFloor(ability.level),
          experienceCeiling: abilityExperienceCeiling(ability.level),
          uses: ability.uses,
          discoveryTick: discovery.tick,
        };
    const techniqueStatus: CodexMonsterView["techniqueStatus"] = technique !== null
      ? "learned"
      : !lore.learned
        ? "studying"
        : outcome?.disposition === "deferred-capacity" && admission === undefined
          ? "held"
          : outcome?.disposition === "rejected"
            ? "rejected"
            : "unverified";
    const visibleOutcome = outcome !== undefined && (
      (techniqueStatus === "held" && outcome.disposition === "deferred-capacity") ||
      (techniqueStatus === "rejected" && outcome.disposition === "rejected")
    ) ? outcome : undefined;
    const discoveryOutcome: CodexDiscoveryOutcomeView | null = visibleOutcome === undefined
      ? null
      : {
          disposition: visibleOutcome.disposition as "deferred-capacity" | "rejected",
          reason: visibleOutcome.reason,
          abilityName: visibleOutcome.abilityName,
          effect: visibleOutcome.mechanics?.effect ?? null,
          manaCost: visibleOutcome.mechanics?.manaCost ?? null,
          potency: visibleOutcome.mechanics?.potency ?? null,
          recordedTick: visibleOutcome.recordedTick,
          thresholdTick: visibleOutcome.thresholdTick,
          repertoireCount: visibleOutcome.repertoireCount,
          repertoireLimit: visibleOutcome.repertoireLimit,
        };
    return {
      monsterId: lore.monsterId,
      monsterName: lore.monsterName,
      visualKey: codexVisualKeys.has(lore.monsterId as CodexVisualKey)
        ? lore.monsterId as CodexVisualKey
        : "unknown",
      encounters: lore.encounters,
      victories: lore.victories,
      insight: lore.insight,
      requiredInsight: lore.requiredInsight,
      remainingVictories: Math.max(0, lore.requiredInsight - lore.insight),
      habit: projectCounterDuelSpeciesHabit(lore.monsterId, lore.encounters),
      techniqueStatus,
      technique,
      discoveryOutcome,
      ...(lore.monsterId === "inkcap-mimic" ? { fieldResearch: inkcapResearch }
        : lore.monsterId === "lantern-wolf" ? { fieldResearch: moonhowlResearch }
          : lore.monsterId === "copperhorn" ? { fieldResearch: copperhornResearch } : {}),
    };
  });
  // Real combat-start already records lore. A legacy/directly witnessed battle
  // can lack that record; show only this evidenced species, without inventing
  // an encounter or victory count, and retain it if its canonical research lasts.
  let addedWitness = false;
  for (const [monsterId, fieldResearch] of [["inkcap-mimic", inkcapResearch], ["lantern-wolf", moonhowlResearch], ["copperhorn", copperhornResearch]] as const) {
    const witnessed = fieldResearch.progress > 0 || state.depth.combat?.combatants.some((unit) => unit.side === "enemies" && unit.speciesId === monsterId) === true;
    if (!witnessed || projected.some((entry) => entry.monsterId === monsterId)) continue;
    const definition = monsterDefinition(monsterId);
    if (definition !== undefined) projected.push({
      monsterId: definition.id, monsterName: definition.name, visualKey: monsterId,
      encounters: 0, victories: 0, insight: 0, requiredInsight: secretTechniqueInsightRequired,
      remainingVictories: secretTechniqueInsightRequired, habit: null,
      techniqueStatus: "studying", technique: null, discoveryOutcome: null, observedOnly: true, fieldResearch,
    });
    addedWitness = true;
  }
  if (addedWitness) {
    projected.sort((left, right) => left.monsterName.localeCompare(right.monsterName, "en", { sensitivity: "base" })
      || (left.monsterId < right.monsterId ? -1 : left.monsterId > right.monsterId ? 1 : 0));
  }
  const monsters = projected.slice(0, maximumCodexEntries);
  return {
    recordedCount: projected.length,
    learnedCount: projected.filter((entry) => entry.techniqueStatus === "learned").length,
    heldCount: projected.filter((entry) => entry.techniqueStatus === "held").length,
    rejectedCount: projected.filter((entry) => entry.techniqueStatus === "rejected").length,
    unverifiedCount: projected.filter((entry) => entry.techniqueStatus === "unverified").length,
    hiddenCount: projected.length - monsters.length,
    monsters,
  };
}

export function projectSpellbookView(state: WorldState): SpellbookViewProjection {
  const sortedAbilities = [...state.depth.hero.abilities].sort((left, right) => {
    const kindOrder = abilityKindOrder[left.kind] - abilityKindOrder[right.kind];
    if (kindOrder !== 0) return kindOrder;
    const nameOrder = left.name.localeCompare(right.name, "en", { sensitivity: "base" });
    return nameOrder !== 0 ? nameOrder : left.id < right.id ? -1 : left.id > right.id ? 1 : 0;
  });
  const uniqueById = new Map<string, typeof sortedAbilities[number]>();
  for (const ability of sortedAbilities) {
    if (!uniqueById.has(ability.id)) uniqueById.set(ability.id, ability);
  }
  const projected = [...uniqueById.values()].map((ability): SpellbookAbilityView => {
    const mastered = ability.level >= 20;
    const experienceFloor = abilityExperienceFloor(ability.level);
    const experienceCeiling = abilityExperienceCeiling(ability.level);
    const masterySpan = mastered ? 1 : Math.max(1, experienceCeiling - experienceFloor);
    const masteryCurrent = mastered
      ? masterySpan
      : Math.max(0, Math.min(masterySpan, ability.experience - experienceFloor));
    const lore = ability.kind === "secret" && ability.sourceMonsterId !== null
      ? state.depth.hero.monsterLore.find(
          (candidate) => candidate.learned
            && candidate.monsterId === ability.sourceMonsterId
            && candidate.secretTechniqueId === ability.id
            && candidate.secretTechniqueName === ability.name,
        )
      : undefined;
    const outcome = (() => {
      if (lore === undefined) return undefined;
      return state.depth.secretDiscoveryOutcomes.find(
        (candidate) => candidate.monsterId === lore.monsterId
          && candidate.monsterName === lore.monsterName
          && candidate.abilityId === ability.id
          && candidate.abilityName === ability.name
          && candidate.disposition !== "rejected",
      );
    })();
    const admission = outcome === undefined
      ? undefined
      : state.depth.secretDiscoveryAdmissions.find((candidate) => candidate.outcomeId === outcome.id);
    const discovery = (() => {
      if (admission === undefined || lore === undefined) return undefined;
      return state.depth.discoveries.find(
        (candidate) => candidate.id === admission.discoveryId
          && candidate.abilityId === ability.id
          && candidate.abilityName === ability.name
          && candidate.monsterId === lore.monsterId
          && candidate.monsterName === lore.monsterName,
      );
    })();
    const provenance = lore === undefined || discovery === undefined
      ? null
      : { monsterName: lore.monsterName, discoveryTick: discovery.tick };
    return {
      id: ability.id,
      name: ability.name,
      kind: ability.kind,
      effect: ability.effect,
      level: ability.level,
      experience: ability.experience,
      experienceFloor,
      experienceCeiling,
      masteryCurrent,
      masterySpan,
      experienceToNext: mastered ? 0 : Math.max(0, experienceCeiling - ability.experience),
      mastered,
      battleUses: ability.uses,
      manaCost: ability.manaCost,
      potency: ability.potency,
      provenanceStatus: ability.kind !== "secret" ? "not-applicable" : provenance === null ? "unverified" : "verified",
      provenance,
    };
  });
  const closest = projected
    .filter((ability) => !ability.mastered)
    .reduce<SpellbookAbilityView | null>(
      (current, ability) => current === null || ability.experienceToNext < current.experienceToNext ? ability : current,
      null,
    );
  const abilities = projected.slice(0, maximumAbilities);
  return {
    abilityCount: projected.length,
    spellCount: projected.filter((ability) => ability.kind === "spell").length,
    techniqueCount: projected.filter((ability) => ability.kind === "technique").length,
    secretCount: projected.filter((ability) => ability.kind === "secret").length,
    masteredCount: projected.filter((ability) => ability.mastered).length,
    totalBattleUses: projected.reduce((total, ability) => total + BigInt(ability.battleUses), 0n).toString(),
    hiddenCount: projected.length - abilities.length,
    closestBreakthrough: closest === null
      ? null
      : {
          abilityId: closest.id,
          abilityName: closest.name,
          nextLevel: closest.level + 1,
          experienceToNext: closest.experienceToNext,
        },
    abilities,
  };
}
